import React, { useEffect, useState } from "react";
import styled from "styled-components";
import FunnelIcon from "@rsuite/icons/Funnel";
import SearchIcon from "@rsuite/icons/Search";
import {
  Input,
  Picker,
  Badge,
  SegmentedPicker,
  SectionRibbon,
  Button,
} from "../../../../ui";

export const _FilterNetworkSystemsFC = ({
  displaySystems,
  systems,
  setDisplaySystems,
}) => {
  const [displayMachines, setDisplayMachines] = useState(displaySystems);
  const [machines, setMachines] = useState(systems);
  const [searchTerm, setSearchTerm] = useState("");

  // Equipment Filtering
  const [customers, setCustomers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [listCustomers, setListCustomers] = useState([]);
  const [listManufacturers, setListManufacturers] = useState([]);
  const [listModalities, setListModalities] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState("All");

  const setFilters = () => {
    setListCustomers([...new Set(systems?.map((item) => item.customer_name))]);
    setListManufacturers([
      ...new Set(systems?.map((item) => item.manufacturer)),
    ]);
    setListModalities([...new Set(systems?.map((item) => item.modality))]);
  };

  const applyFilters = () => {
    let tempMachines = machines?.filter((machine) => {
      const customerMatch =
        customers.length === 0 || customers.includes(machine.customer_name);
      const manufacturerMatch =
        manufacturers.length === 0 ||
        manufacturers.includes(machine.manufacturer);
      const modalityMatch =
        modalities.length === 0 || modalities.includes(machine.modality);
      const visibilityMatch =
        visibilityFilter === "All" ||
        (visibilityFilter === "Visible" && machine.show_on_website) ||
        (visibilityFilter === "Hidden" && !machine.show_on_website);

      const searchMatch =
        !searchTerm ||
        Object.values(machine).some((field) =>
          String(field)
            .toLowerCase()
            .includes(String(searchTerm).toLowerCase()),
        );

      return (
        customerMatch &&
        manufacturerMatch &&
        modalityMatch &&
        visibilityMatch &&
        searchMatch
      );
    });

    setDisplaySystems(tempMachines);
    setDisplayMachines(tempMachines);
  };

  const handleSearch = (value) => {
    // Handle both direct values and event objects
    const actualValue = value?.target ? value.target.value : value;
    setSearchTerm(actualValue || "");
  };

  const clearAllFilters = () => {
    setCustomers([]);
    setManufacturers([]);
    setModalities([]);
    setVisibilityFilter("All");
    setSearchTerm("");
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (customers.length > 0) count++;
    if (manufacturers.length > 0) count++;
    if (modalities.length > 0) count++;
    if (visibilityFilter !== "All") count++;
    if (searchTerm) count++;
    return count;
  };

  useEffect(() => {
    setFilters();
    setMachines(systems);
  }, [systems]);

  useEffect(() => {
    applyFilters();
  }, [
    customers,
    manufacturers,
    modalities,
    visibilityFilter,
    searchTerm,
    machines,
  ]);

  return (
    <FilterContainer>
      <SectionRibbon
        title={`${displayMachines?.length || 0} of ${systems?.length || 0} systems`}
        icon={FunnelIcon}
        variant="primary"
        rightContent={
          <RightContentWrapper>
            <SearchInput>
              <Input
                placeholder="Search systems..."
                value={searchTerm}
                onChange={handleSearch}
                icon={<SearchIcon />}
                clearable
                onClear={() => setSearchTerm("")}
              />
            </SearchInput>
            {getActiveFilterCount() > 0 && (
              <ActiveFiltersBadge variant="primary">
                {getActiveFilterCount()} Active Filter
                {getActiveFilterCount() > 1 ? "s" : ""}
              </ActiveFiltersBadge>
            )}
          </RightContentWrapper>
        }
      />

      <FilterContent>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel></FilterLabel>
            <SegmentedPicker
              options={[
                { eventKey: "All", label: "All" },
                { eventKey: "Visible", label: "Visible" },
                { eventKey: "Hidden", label: "Hidden" },
              ]}
              activeKey={visibilityFilter}
              onSelect={setVisibilityFilter}
              size="small"
              variant="subtle"
            />
          </FilterGroup>

          {listCustomers.length > 1 && (
            <FilterGroup>
              <Picker
                data={listCustomers.map((item) => ({
                  label: item,
                  value: item,
                }))}
                value={customers}
                onChange={setCustomers}
                placeholder="All Customers"
                native={false}
                multiple
                searchable
                width="200px"
              />
            </FilterGroup>
          )}

          {listManufacturers.length > 1 && (
            <FilterGroup>
              <Picker
                data={listManufacturers.map((item) => ({
                  label: item,
                  value: item,
                }))}
                value={manufacturers}
                onChange={setManufacturers}
                placeholder="All Manufacturers"
                native={false}
                multiple
                searchable
                width="200px"
              />
            </FilterGroup>
          )}

          {listModalities.length > 1 && (
            <FilterGroup>
              <Picker
                data={listModalities.map((item) => ({
                  label: item,
                  value: item,
                }))}
                value={modalities}
                onChange={setModalities}
                placeholder="All Modalities"
                native={false}
                multiple
                searchable
                width="200px"
              />
            </FilterGroup>
          )}

          {getActiveFilterCount() > 0 && (
            <ClearButtonWrapper>
              <Button size="small" variant="subtle" onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </ClearButtonWrapper>
          )}
        </FilterGrid>
      </FilterContent>
    </FilterContainer>
  );
};

// Styled Components
const FilterContainer = styled.div`
  width: 100%;
`;

const RightContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    width: 100%;
  }
`;

const ActiveFiltersBadge = styled(Badge)`
  font-size: 0.75rem;
  white-space: nowrap;
`;

const FilterContent = styled.div`
  padding: 1.5rem;
  padding-top: 1rem;
`;

const SearchInput = styled.div`
  min-width: 500px;
  max-width: 500px;

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClearButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  padding-bottom: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ClearButton = styled.button`
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;
